import type { NotificationMessage } from '../common/types';

/** Состояние строки, с которым сравнивается пришедшее сообщение */
export interface AppliedNotificationState {
  group_count?: number | null;
  last_event_at?: Date | string | null;
}

/**
 * Выбрать самое свежее из известных состояний строки.
 *
 * Уведомление живёт в двух местах: в ленте (последние 50 строк) и в сводке
 * непрочитанных. Вытесненное из ленты остаётся в сводке, и сравнение только
 * с лентой пропустило бы отставшее сообщение — оно откатило бы счётчик
 * группы, а вместе с ним и бейдж раздела.
 *
 * @param candidates Состояния из разных источников; пустые игнорируются
 */
export function pickAppliedState(
  ...candidates: Array<AppliedNotificationState | undefined>
): AppliedNotificationState | undefined {
  return candidates.reduce<AppliedNotificationState | undefined>((freshest, candidate) => {
    if (!candidate) return freshest;
    if (!freshest) return candidate;

    return compareFreshness(candidate, freshest) > 0 ? candidate : freshest;
  }, undefined);
}

/**
 * Устарело ли сообщение относительно уже применённого состояния.
 *
 * Слияния одной группы идут параллельно, и порядок их отправки в сокет не
 * гарантирован: событие с `group_count = 2` может прийти после события с 3.
 * Применить его — значит откатить счётчик группы назад до перезагрузки
 * страницы, поэтому отставшие сообщения отбрасываются.
 *
 * @param applied Известное состояние строки (его нет — сообщение свежее)
 * @param incoming Пришедшее сообщение
 */
export function isStaleNotificationMessage(
  applied: AppliedNotificationState | undefined,
  incoming: NotificationMessage
): boolean {
  if (!applied) {
    return false;
  }

  return compareFreshness(incoming, applied) < 0;
}

/**
 * Сравнить свежесть двух состояний: > 0 — первое новее.
 *
 * Основной признак — счётчик группы; при равенстве решает время последнего
 * события. У уведомлений без дедупликации счётчик всегда 1, и отличить их
 * версии можно только по времени.
 */
function compareFreshness(a: AppliedNotificationState, b: AppliedNotificationState): number {
  const countA = a.group_count ?? 1;
  const countB = b.group_count ?? 1;

  if (countA !== countB) {
    return countA - countB;
  }

  const timeA = toTime(a.last_event_at);
  const timeB = toTime(b.last_event_at);

  if (timeA === null || timeB === null) {
    return 0; // не с чем сравнивать — состояния считаем равнозначными
  }

  return timeA - timeB;
}

/** Отметка времени в миллисекундах; `null` — значения нет или оно нечитаемо */
function toTime(value: Date | string | null | undefined): number | null {
  if (!value) return null;

  const time = value instanceof Date ? value.getTime() : new Date(value).getTime();
  return Number.isNaN(time) ? null : time;
}
